// Natural-hours drift: engine-assigned slots get a minute offset past the
// slot hour so an account never posts at exactly :00 every day (18:00,
// 18:00, 18:00 reads as a bot; 18:07, 18:19, 18:04 reads as a person).
//
// The offset is deterministic — seeded from the video id — so re-running
// distribution in a session never shuffles times, and dirty-state
// comparisons stay stable. Seeding per video (not per publication) makes a
// cross-posted video launch at the same minute on every platform. That can
// never synchronize two posts on ONE platform, because a video holds at
// most one account per platform — enforced by distribution-pool election
// (elect.ts) and the per-video selector rule. If that invariant ever
// relaxes, same-platform posts must go back to per-publication staggering.
// Drift is strictly forward (within the same hour): it never lands before
// the hour the user picked, and normalizeToSlotStart still floors back to
// the same base slot, so occupancy math is unaffected.
//
// Anchored publications and user-picked custom datetimes are never drifted —
// callers only apply drift to slots freshly returned by nextAvailableSlot.

// Single-digit minutes only: 10:07 still reads as "the 10 PM you picked",
// while 10:26 reads as a mismatch. 8 seed-driven values keep enough
// variation to never repeat exact times day after day.
const MIN_DRIFT_MINUTES = 2;
const MAX_DRIFT_MINUTES = 9;

/** FNV-1a over the seed string — stable across sessions and platforms. */
function hashSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function driftMinutesForSeed(seed: string): number {
  const span = MAX_DRIFT_MINUTES - MIN_DRIFT_MINUTES + 1;
  return MIN_DRIFT_MINUTES + (hashSeed(seed) % span);
}

/**
 * Returns the slot shifted a few deterministic minutes past its hour.
 * The input slot is expected at :00 (nextAvailableSlot output); the result
 * stays inside the same hour so all slot-level invariants keep holding.
 */
export function applyNaturalDrift(slot: Date, seed: string): Date {
  const drifted = new Date(slot);
  drifted.setMinutes(driftMinutesForSeed(seed), 0, 0);
  return drifted;
}
